/**
 * PostHog environment helpers.
 *
 * Centralises the "should we enable PostHog?" decision so the provider,
 * tests, and any future server-side usage share one source of truth.
 */

/** Default PostHog ingest host (US Cloud). */
export const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/** Env vars required for PostHog to initialise. */
export interface PostHogEnv {
  key: string;
  host: string;
}

/**
 * Returns the PostHog env vars when they are set, or `null` otherwise.
 * Keeps the provider from crashing when the vars are absent (local dev,
 * CI, environments that intentionally omit them).
 */
export function getPostHogEnv(): PostHogEnv | null {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  if (!key) return null;
  return { key, host: host || DEFAULT_POSTHOG_HOST };
}

/**
 * Whether PostHog should be active in the current environment.
 *
 * Gating logic (in priority order):
 *
 * 1. If `NEXT_PUBLIC_POSTHOG_KEY` is missing → disabled (nothing to init).
 * 2. If `NEXT_PUBLIC_VERCEL_ENV` is set (Vercel deploys) → enabled for
 *    `"production"` and `"preview"`.  Disabled for `"development"`
 *    (`vercel dev`).
 * 3. If a manual override `NEXT_PUBLIC_ENABLE_POSTHOG` is set → honour it
 *    (truthy = enabled).  Useful for non-Vercel hosting or local testing.
 * 4. Fallback → disabled (local `next dev`, CI, etc.).
 *
 * PostHog is HIPAA-compliant and we have a BAA/DPA in place, so production
 * access is intentional.  The env-var gating keeps PostHog off in local dev
 * and CI where the key is not set, and prevents accidental exposure of the
 * project API key to coding agents or other non-deploy contexts.
 */
export function isPostHogEnabled(): boolean {
  if (!getPostHogEnv()) return false;

  // Vercel sets NEXT_PUBLIC_VERCEL_ENV automatically on every deployment.
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
  if (vercelEnv) {
    return vercelEnv === "production" || vercelEnv === "preview";
  }

  // Manual override for non-Vercel environments.
  const flag = process.env.NEXT_PUBLIC_ENABLE_POSTHOG;
  if (flag !== undefined && flag !== "") {
    return ["true", "1", "yes", "on"].includes(flag.toLowerCase());
  }

  return false;
}
